import { Icon } from "@/components/ui/Icon";
import { Price } from "@/components/ui/Price";
import { Text } from "@/components/ui/Typography";
import { Plus } from "@/icons";
import type { Price as PriceModel } from "@/types";

type OptionPriceProps = {
  price: PriceModel;
  quantity: number;
  priceMethod?: "SUM" | "MAX" | "AVG";
  maxPrice?: number;
  isPortionGroup?: boolean;
  portionTarget?: number;
  portionFractionBase?: number;
  rawQuantity?: number;
};

type Resolved = { amount: number; active: boolean };

function resolvePortionPrice({
  price,
  quantity,
  priceMethod,
  maxPrice,
  portionTarget,
  portionFractionBase,
  rawQuantity,
}: OptionPriceProps & { portionTarget: number }): Resolved {
  const idle = { amount: price.value, active: false };

  if (priceMethod === "MAX") {
    if (quantity <= 0) return idle;
    const effectivePrice = Math.max(maxPrice ?? 0, price.value);
    const fractionBase = portionFractionBase && portionFractionBase > 0 ? portionFractionBase : portionTarget;
    const numerator = rawQuantity && rawQuantity > 0 ? rawQuantity : quantity;
    return { amount: effectivePrice * (numerator / fractionBase), active: true };
  }

  if (quantity <= 0) return idle;
  return { amount: (price.value * quantity) / portionTarget, active: true };
}

function resolvePrice(props: OptionPriceProps): Resolved {
  const { price, quantity, priceMethod, isPortionGroup, portionTarget } = props;

  if (isPortionGroup && portionTarget && (priceMethod === "MAX" || priceMethod === "AVG")) {
    return resolvePortionPrice({ ...props, portionTarget });
  }

  if (priceMethod === "MAX" || priceMethod === "AVG") {
    return { amount: price.value, active: quantity > 0 };
  }

  return { amount: price.value * (quantity || 1), active: Boolean(quantity) };
}

/** "+ R$ x" add-on price of an option; turns secondary once the option is selected. */
export default function OptionPrice(props: OptionPriceProps) {
  if (props.price.value === 0) return null;

  const { amount, active } = resolvePrice(props);

  return (
    <Text weight="bold" tone={active ? "secondary" : "muted"} className="flex items-center gap-2">
      <Icon as={Plus} size="sm" weight="bold" />
      <Price value={amount} />
    </Text>
  );
}
